const INGREDIENTS = {
	meats: [],
	veggies: [],
	organs: [],
	bones: [],
};
const BLENDS_BY_TYPE = window.recipeMakerBlendsByType || {};

const INGREDIENTS_FROM_METAOBJECTS = window.recipeMakerIngredientsFromMetaobjects || {};
['meats', 'veggies', 'organs', 'bones'].forEach((category) => {
	INGREDIENTS[category] = Array.isArray(INGREDIENTS_FROM_METAOBJECTS[category]) ? INGREDIENTS_FROM_METAOBJECTS[category] : [];
});

const BODY_CONDITIONS_SOURCE = window.recipeMakerBodyConditions || {};
const BODY_CONDITIONS_DATA = Object.entries(BODY_CONDITIONS_SOURCE).map(([value, data]) => ({
	value,
	adj: Number(data?.adj) || 0,
	label: data?.label || '',
	note: data?.note || '',
	image: data?.image || '',
}));

// Weight guide copy comes only from the dog_details block (theme / template JSON) — see window.recipeMakerWeightGuideHtml
const WEIGHT_GUIDE_HTML = window.recipeMakerWeightGuideHtml || {};

const BODY_CONDITIONS = BODY_CONDITIONS_DATA.reduce((accumulator, item) => {
	accumulator[item.value] = Number(item.adj) || 0;
	return accumulator;
}, {});

const DEFAULT_PROPORTIONS = window.recipeMakerDefaultProportions || {};
const ORDER_CATS = ['meats', 'veggies', 'organs', 'bones'];
const TYPE_TO_CATEGORY = { meat: 'meats', veg: 'veggies', organ: 'organs', bone: 'bones' };
const CATEGORY_TO_TYPE = { meats: 'meat', veggies: 'veg', organs: 'organ', bones: 'bone' };
const CATEGORY_ENTRY_SCREEN = { meats: 2, veggies: 3, organs: 4, bones: 5 };
const POINTER_ACTIVATION_MAX_MOVE_PX = 12;
const RECIPE_EMAIL_CONFIG = window.recipeMakerRecipeEmail || {};
const IS_THEME_EDITOR = !!(window.Shopify && window.Shopify.designMode);
const CONVERT_RECIPE_MAKER_START_CONVERSION = '100140583';
const CONVERT_RECIPE_MAKER_FINISH_CONVERSION = '100140595';

// Step URLs use a ?step= query param because Shopify 404s any sub-path of a page.
const STEP_PARAM = 'step';
const SCREEN_SLUGS = {
	0: 'landing',
	1: 'intro',
	2: 'meats',
	3: 'veggies',
	4: 'organs',
	5: 'bones',
	6: 'dog-details',
	7: 'finish-up',
	8: 'your-recipe',
	9: 'thank-you',
};

// Screens 8-9 are generated from state: their server markup still holds [[name]]
// placeholders until renderRecipe/syncThankYouScreenFromState fills them in, so they
// must never be shown before that runs.
const FIRST_GENERATED_SCREEN = 8;

// Screens 8-9 are only reachable after buildAndShowRecipe (or a ?recipe= share link) in this page load.
let recipeGenerated = false;
let lastTrackedStepScreen = null;

function triggerRecipeMakerConvertConversion(conversionId) {
	if (!conversionId || IS_THEME_EDITOR) {
		return;
	}

	window._conv_q = window._conv_q || [];
	window._conv_q.push(['triggerConversion', conversionId]);
}

// ============================================================
// DR. DOBIAS RECIPE MAKER — App Logic
// ============================================================

const I18N = window.recipeMakerText || {};

// ---- STATE ----
const state = {
	currentScreen: 0,
	selected: { meats: [], veggies: [], organs: [], bones: [] },
	dog: {
		name: '',
		gender: 'Male',
		isFeedingRaw: false,
		weight: null,
		weightUnit: 'lbs',
		age: 'adult',
		bodyCondition: 'healthy-weight',
		bodyAdj: 0,
	},
	recipe: { name: '', story: '', newsletter: 'no', email: '', customerFirstName: '', customerLastName: '' },
	days: 1,
	currency: 'USD',
	proportions: { ...DEFAULT_PROPORTIONS },
	dismissedZeroPortion: { meats: false, veggies: false, organs: false, bones: false },
	skippedStepCategories: { meats: false, veggies: false, organs: false, bones: false },
};

// ---- PERSISTENCE ----
const STORAGE_VERSION = 1;
const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const STORAGE_KEY = `pd_recipe_maker_state:${window.location.pathname}`;

let persistStateTimer = null;

function getPersistableState() {
	return {
		currentScreen: state.currentScreen,
		selected: {
			meats: [...state.selected.meats],
			veggies: [...state.selected.veggies],
			organs: [...state.selected.organs],
			bones: [...state.selected.bones],
		},
		dog: { ...state.dog },
		recipe: { ...state.recipe },
		days: state.days,
		proportions: { ...state.proportions },
		dismissedZeroPortion: { ...state.dismissedZeroPortion },
		skippedStepCategories: { ...state.skippedStepCategories },
		recipeGenerated,
	};
}

function sanitizeRestoredState(data) {
	if (!data || typeof data !== 'object') {
		return null;
	}

	const sanitized = {
		currentScreen: Math.max(0, Math.min(9, Number(data.currentScreen) || 0)),
		selected: { meats: [], veggies: [], organs: [], bones: [] },
		dog: {
			name: '',
			gender: 'Male',
			isFeedingRaw: false,
			weight: null,
			weightUnit: 'lbs',
			age: 'adult',
			bodyCondition: 'healthy-weight',
			bodyAdj: 0,
		},
		recipe: { name: '', story: '', newsletter: 'no', email: '', customerFirstName: '', customerLastName: '' },
		days: Math.max(1, Math.min(14, Number(data.days) || 1)),
		proportions: { ...DEFAULT_PROPORTIONS },
		dismissedZeroPortion: { meats: false, veggies: false, organs: false, bones: false },
		skippedStepCategories: { meats: false, veggies: false, organs: false, bones: false },
		recipeGenerated: !!data.recipeGenerated,
	};

	if (data.dog && typeof data.dog === 'object') {
		Object.assign(sanitized.dog, data.dog);
		const bodyCondition = sanitized.dog.bodyCondition || 'healthy-weight';

		sanitized.dog.bodyAdj = BODY_CONDITIONS[bodyCondition] ?? Number(sanitized.dog.bodyAdj) ?? 0;
	}

	if (data.recipe && typeof data.recipe === 'object') {
		Object.assign(sanitized.recipe, data.recipe);
	}

	ORDER_CATS.forEach((cat) => {
		const validIds = new Set((INGREDIENTS[cat] || []).map((item) => item.id));
		const saved = Array.isArray(data.selected?.[cat]) ? data.selected[cat] : [];

		sanitized.selected[cat] = saved.filter((id) => validIds.has(id));
	});

	if (data.proportions && typeof data.proportions === 'object') {
		ORDER_CATS.forEach((cat) => {
			const val = Number(data.proportions[cat]);

			if (!Number.isNaN(val)) {
				sanitized.proportions[cat] = Math.max(0, Math.min(100, val));
			}
		});
	}

	if (data.dismissedZeroPortion && typeof data.dismissedZeroPortion === 'object') {
		ORDER_CATS.forEach((cat) => {
			sanitized.dismissedZeroPortion[cat] = !!data.dismissedZeroPortion[cat];
		});
	}

	if (data.skippedStepCategories && typeof data.skippedStepCategories === 'object') {
		ORDER_CATS.forEach((cat) => {
			sanitized.skippedStepCategories[cat] = !!data.skippedStepCategories[cat];
		});
	}

	return sanitized;
}

function saveRecipeMakerState() {
	if (IS_THEME_EDITOR) {
		return;
	}

	clearTimeout(persistStateTimer);
	persistStateTimer = setTimeout(() => {
		try {
			const envelope = {
				v: STORAGE_VERSION,
				savedAt: Date.now(),
				data: getPersistableState(),
			};

			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
		} catch (_error) {
			// private mode / quota
		}
	}, 150);
}

function loadRecipeMakerState() {
	if (IS_THEME_EDITOR) {
		return null;
	}

	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);

		if (!raw) {
			return null;
		}

		const envelope = JSON.parse(raw);

		if (!envelope || envelope.v !== STORAGE_VERSION) {
			return null;
		}

		if (STORAGE_TTL_MS > 0 && envelope.savedAt && Date.now() - envelope.savedAt > STORAGE_TTL_MS) {
			clearRecipeMakerState();

			return null;
		}

		return sanitizeRestoredState(envelope.data);
	} catch (_error) {
		return null;
	}
}

function clearRecipeMakerState() {
	if (IS_THEME_EDITOR) {
		return;
	}

	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch (_error) {
		// ignore
	}
}

function applyRestoredState(data) {
	state.currentScreen = data.currentScreen;
	state.selected = data.selected;
	state.dog = data.dog;
	state.recipe = data.recipe;
	state.days = data.days;
	state.proportions = data.proportions;
	state.dismissedZeroPortion = data.dismissedZeroPortion;
	state.skippedStepCategories = data.skippedStepCategories;
	recipeGenerated = data.recipeGenerated;
}

function syncToggleGroupByValue(groupEl, value) {
	if (!groupEl || value == null) {
		return;
	}

	groupEl.querySelectorAll('.toggle-btn').forEach((btn) => {
		btn.classList.toggle('active', btn.dataset.value === String(value));
	});
}

function syncThankYouScreenFromState() {
	const dogName = state.dog.name || I18N.defaultDogName || '';
	const nameEl = document.getElementById('thankyouDogName');
	const subtitleEl = document.getElementById('thankyouSubtitleText');

	if (nameEl) {
		nameEl.textContent = dogName;
	}

	if (subtitleEl) {
		const template = subtitleEl.getAttribute('data-template') || '';

		if (template) {
			subtitleEl.textContent = template.replace(/\[\[name\]\]/g, dogName);
		}
	}
}

function hydrateUIFromState() {
	const setVal = (id, val) => {
		const el = document.getElementById(id);

		if (el != null && val != null && val !== '') {
			el.value = val;
		}
	};

	setVal('dogName', state.dog.name);

	if (state.dog.weight != null) {
		setVal('dogWeight', state.dog.weight);
	}

	setVal('customerFirstName', state.recipe.customerFirstName);
	setVal('customerLastName', state.recipe.customerLastName);
	setVal('recipeName', state.recipe.name);
	setVal('dogStory', state.recipe.story);
	setVal('newsletterEmail', state.recipe.email);

	const screen6 = document.getElementById('screen6');

	if (screen6) {
		const toggleGroups = screen6.querySelectorAll('.toggle-group');

		if (toggleGroups[0]) {
			syncToggleGroupByValue(toggleGroups[0], state.dog.gender);
		}

		if (toggleGroups[1]) {
			syncToggleGroupByValue(toggleGroups[1], state.dog.weightUnit);
		}

		if (toggleGroups[2]) {
			syncToggleGroupByValue(toggleGroups[2], state.dog.age);
		}
	}

	document.querySelectorAll('.condition-card').forEach((card) => {
		const isActive = card.dataset.value === (state.dog.bodyCondition || 'healthy-weight');

		card.classList.toggle('active', isActive);
	});
	applyWeightGuideForValue(state.dog.bodyCondition || 'healthy-weight');
	syncFeedingExperienceToggle();

	const newsletterOptIn = document.getElementById('newsletterOptIn');

	if (newsletterOptIn) {
		newsletterOptIn.checked = state.recipe.newsletter === 'yes';
	}

	syncDayCounters();
	updateCounts();
	ORDER_CATS.forEach((cat) => updateStepPortionUI(cat));
	updateSkipNextButtons();

	if (state.currentScreen === 8) {
		renderRecipe();
	} else if (state.currentScreen === 9) {
		syncThankYouScreenFromState();
	}
}

// ---- CURRENCY DETECTION ----
// Detect Shopify market currency from meta tag or global Shopify object
function detectCurrency() {
	try {
		if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
			state.currency = window.Shopify.currency.active;
		} else {
			const metaCurrency = document.querySelector('meta[name="currency"]');
			if (metaCurrency) state.currency = metaCurrency.getAttribute('content');
			else {
				const lang = navigator.language || 'en-US';
				state.currency = lang.startsWith('en-CA') ? 'CAD' : 'USD';
			}
		}
	} catch (e) {
		state.currency = 'USD';
	}
	const badge = document.getElementById('currencyBadge');
	if (badge) badge.textContent = state.currency;
}

// ---- STEP URLS, HISTORY & STEP EVENTS ----
function getStepSlugForScreen(n) {
	return SCREEN_SLUGS[n] || SCREEN_SLUGS[0];
}

function getScreenForStepSlug(slug) {
	if (!slug) {
		return null;
	}

	const normalized = String(slug).toLowerCase();
	const entry = Object.keys(SCREEN_SLUGS).find((key) => SCREEN_SLUGS[key] === normalized);

	return entry == null ? null : Number(entry);
}

function buildUrlForScreen(n) {
	const params = new URLSearchParams(window.location.search);

	// A lingering ?recipe= would win over ?step= on reload and jump to the shared recipe.
	params.delete('recipe');

	if (n >= 1) {
		params.set(STEP_PARAM, getStepSlugForScreen(n));
	} else {
		params.delete(STEP_PARAM);
	}

	const qs = params.toString();

	return `${window.location.pathname}${qs ? `?${qs}` : ''}`;
}

/**
 * modes: 'push' (user navigation), 'replace' (initial load / URL correction),
 * 'keep' (leave the URL exactly as-is, e.g. ?recipe= share links and blocked deep links).
 */
function syncHistoryForScreen(n, mode = 'push') {
	if (IS_THEME_EDITOR || !window.history || typeof window.history.pushState !== 'function') {
		return;
	}

	if (mode === 'keep') {
		window.history.replaceState({ rmScreen: n }, '', window.location.href);

		return;
	}

	const url = buildUrlForScreen(n);
	const current = `${window.location.pathname}${window.location.search}`;

	if (mode === 'replace' || url === current) {
		window.history.replaceState({ rmScreen: n }, '', url);
	} else {
		window.history.pushState({ rmScreen: n }, '', url);
	}
}

function trackRecipeMakerStep(n) {
	if (IS_THEME_EDITOR || n === lastTrackedStepScreen) {
		return;
	}

	lastTrackedStepScreen = n;

	if (typeof gtag !== 'function') {
		return;
	}

	gtag('event', 'recipe_maker_step', {
		recipe_maker_step: getStepSlugForScreen(n),
		recipe_maker_step_number: n,
	});
}

// Mirrors validateAndNext: a category can be passed when something is selected or it is optional.
function isIngredientCategoryPassable(cat) {
	const validation = (window.recipeMakerScreen8 || {}).ingredientValidation || {};
	const isMandatory = !!validation[CATEGORY_TO_TYPE[cat]]?.isMandatory;

	return (state.selected[cat] || []).length > 0 || !isMandatory;
}

function areDogDetailsComplete() {
	return (
		String(state.recipe.customerFirstName || '').trim() !== '' &&
		String(state.dog.name || '').trim() !== '' &&
		Number(state.dog.weight) > 0
	);
}

function getMaxReachableScreen() {
	let max = 2;

	for (const cat of ORDER_CATS) {
		if (!isIngredientCategoryPassable(cat)) {
			return max;
		}

		max = CATEGORY_ENTRY_SCREEN[cat] + 1;
	}

	if (!areDogDetailsComplete()) {
		return 6;
	}

	if (!recipeGenerated) {
		return 7;
	}

	return 9;
}

/** Fills in a generated screen's placeholders before it becomes visible. */
function renderGeneratedScreen(screen) {
	if (screen === 8) {
		renderRecipe();
	} else if (screen === 9) {
		syncThankYouScreenFromState();
	}
}

/**
 * Which screen to show underneath the resume guard. The requested step normally stays
 * visible so the view matches the URL, but the generated screens have nothing real to
 * show without state — they would display raw [[name]] placeholders — so those fall
 * back to where the visitor can actually go.
 */
function getBlockedDisplayScreen(requestedScreen, fallbackScreen) {
	return requestedScreen >= FIRST_GENERATED_SCREEN ? fallbackScreen : requestedScreen;
}

function hasRecipeProgress() {
	return (
		ORDER_CATS.some((cat) => (state.selected[cat] || []).length > 0) ||
		String(state.dog.name || '').trim() !== '' ||
		String(state.recipe.customerFirstName || '').trim() !== ''
	);
}

let resumeGuardKeydownHandler = null;

function closeResumeGuardModal() {
	const modal = document.getElementById('resumeGuardModal');

	if (modal) {
		modal.remove();
	}

	if (resumeGuardKeydownHandler) {
		document.removeEventListener('keydown', resumeGuardKeydownHandler);
		resumeGuardKeydownHandler = null;
	}
}

/**
 * Where the visitor can legitimately go. Must be read *before* the blocked step is
 * displayed, because showing it moves state.currentScreen to that step.
 */
function getResumeGuardOptions() {
	const resumeTarget = Math.min(state.currentScreen, getMaxReachableScreen());

	return { resumeTarget, canContinue: hasRecipeProgress() && resumeTarget >= 2 };
}

/**
 * Shown when the URL points at a step the current state cannot support (deep link,
 * or back/forward into a cleared flow). The requested step stays visible underneath
 * and the URL is left as landed, so heatmaps and session recordings still match the
 * URL; the visitor picks where to actually go.
 */
function showResumeGuardModal(requestedScreen, options) {
	const recipeMakerRoot = document.querySelector('.recipe-maker-section');

	if (!recipeMakerRoot) {
		return;
	}

	closeResumeGuardModal();

	const { resumeTarget, canContinue } = options || getResumeGuardOptions();
	const showContinue = canContinue;
	const modal = document.createElement('div');

	modal.id = 'resumeGuardModal';
	modal.className = 'modal-overlay';
	modal.innerHTML = `
          <div class="modal-box reset-modal-box">
            <h3>${I18N.resumeGuardTitle || ''}</h3>
            <p>${I18N.resumeGuardSubtitle || ''}</p>
            <div class="reset-options">
              ${
					showContinue
						? `
              <button type="button" class="btn-reset-option text-left" data-resume-guard-continue>
                <div class="reset-opt-icon">🐾</div>
                <div>
                  <div class="reset-opt-title">${I18N.resumeGuardContinueTitle || ''}</div>
                  <div class="reset-opt-desc">${I18N.resumeGuardContinueDesc || ''}</div>
                </div>
              </button>`
						: ''
				}
              <button type="button" class="btn-reset-option text-left" data-resume-guard-restart>
                <div class="reset-opt-icon">🐶</div>
                <div>
                  <div class="reset-opt-title">${I18N.resumeGuardRestartTitle || ''}</div>
                  <div class="reset-opt-desc">${I18N.resumeGuardRestartDesc || ''}</div>
                </div>
              </button>
            </div>
          </div>
        `;
	recipeMakerRoot.appendChild(modal);

	const resolveTo = (screen) => {
		closeResumeGuardModal();
		goToScreen(screen);
	};
	const continueBtn = modal.querySelector('[data-resume-guard-continue]');

	if (continueBtn) {
		continueBtn.addEventListener('click', () => resolveTo(resumeTarget));
	}

	modal.querySelector('[data-resume-guard-restart]').addEventListener('click', () => resolveTo(0));

	// Dismissing resolves to the primary option: the step underneath is only there to
	// match the URL, so nobody should be left on a step they cannot actually use.
	modal.addEventListener('click', (e) => {
		if (e.target === modal) {
			resolveTo(showContinue ? resumeTarget : 0);
		}
	});

	resumeGuardKeydownHandler = (e) => {
		if (e.code && e.code.toUpperCase() === 'ESCAPE') {
			resolveTo(showContinue ? resumeTarget : 0);
		}
	};
	document.addEventListener('keydown', resumeGuardKeydownHandler);
}

/** null means "no step requested" (plain page URL, or a ?recipe= link that handles its own restore on load). */
function getRequestedScreenFromLocation() {
	const params = new URLSearchParams(window.location.search);

	if (params.get('recipe')) {
		return recipeGenerated ? 8 : null;
	}

	return getScreenForStepSlug(params.get(STEP_PARAM));
}

function handleRecipeMakerPopstate() {
	if (IS_THEME_EDITOR) {
		return;
	}

	const requested = getRequestedScreenFromLocation();
	const target = requested == null ? 0 : requested;

	if (target === state.currentScreen) {
		return;
	}

	if (target > getMaxReachableScreen()) {
		// Read the resume options first, then show the blocked step so the view matches the URL.
		const resumeOptions = getResumeGuardOptions();
		const displayScreen = getBlockedDisplayScreen(target, resumeOptions.resumeTarget);

		renderGeneratedScreen(displayScreen);
		goToScreen(displayScreen, { fromHistory: true, instant: true, skipPersist: true });
		showResumeGuardModal(target, resumeOptions);

		return;
	}

	closeResumeGuardModal();
	// Re-render before showing, in case state changed since the screen was last seen.
	renderGeneratedScreen(target);
	goToScreen(target, { fromHistory: true });
}

window.addEventListener('popstate', handleRecipeMakerPopstate);

// ---- SCREEN NAVIGATION ----
const TOTAL_STEPS = 4; // for progress bar (ingredient group counts as 1)

function scrollIngredientStepProgressIntoView(options = {}) {
	const activeScreen = document.querySelector('.recipe-maker-section .screen.active');

	if (!activeScreen) {
		return;
	}

	const scrollContainer = activeScreen.querySelector('.ingredient-step-progress');
	const activeStep = scrollContainer?.querySelector('.ingredient-step-progress__step.is-active');
	const list = scrollContainer?.querySelector('.ingredient-step-progress__list');

	if (!scrollContainer || !activeStep || !list) {
		return;
	}

	const behavior = options.instant ? 'auto' : 'smooth';
	const containerWidth = scrollContainer.clientWidth;
	const maxScroll = Math.max(0, scrollContainer.scrollWidth - containerWidth);
	const isFirstStep = activeStep === list.querySelector('.ingredient-step-progress__step');
	const isLastStep = activeStep === list.querySelector('.ingredient-step-progress__step:last-of-type');

	let targetScroll = 0;

	if (isLastStep) {
		targetScroll = maxScroll;
	} else if (isFirstStep) {
		targetScroll = 0;
	} else {
		const edgePadding = 12;
		const stepLeft = activeStep.offsetLeft;
		const stepWidth = activeStep.offsetWidth;
		const idealScroll = stepLeft + stepWidth / 2 - containerWidth / 2;
		const minScroll = Math.max(0, stepLeft - edgePadding);
		const maxScrollForStep = Math.min(maxScroll, stepLeft + stepWidth + edgePadding - containerWidth);

		targetScroll = Math.max(minScroll, Math.min(idealScroll, maxScrollForStep));
	}

	scrollContainer.scrollTo({
		left: targetScroll,
		behavior,
	});
}

function goToScreen(n, options = {}) {
	document.querySelectorAll('.recipe-maker-section .screen.active').forEach((screen) => {
		screen.classList.remove('active');
	});

	state.currentScreen = n;

	const next = document.getElementById(`screen${n}`);

	if (!next) {
		return;
	}

	next.classList.add('active');

	const scrollBehavior = options.instant ? 'auto' : 'smooth';

	next.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
	window.scrollTo({ top: 0, behavior: scrollBehavior });

	Object.keys(CATEGORY_ENTRY_SCREEN).forEach((cat) => {
		if (n === CATEGORY_ENTRY_SCREEN[cat]) {
			state.skippedStepCategories[cat] = false;
		}
	});

	// Show/hide progress bar
	const pw = document.getElementById('progressWrap');
	if (pw) pw.style.display = n >= 1 && n <= 8 ? 'block' : 'none';
	if (n >= 1 && n <= 8) updateProgress(n);

	const screenToCategory = { 2: 'meats', 3: 'veggies', 4: 'organs', 5: 'bones' };
	const activeCategory = screenToCategory[n];
	if (activeCategory) updateStepPortionUI(activeCategory);

	if (n === 6) {
		applyWeightGuideForValue(state.dog.bodyCondition || 'healthy-weight');
	}

	updateSkipNextButtons();
	initStickyNavRows();

	requestAnimationFrame(() => {
		document.querySelectorAll(STICKY_BAR_ACTIVE_SELECTOR).forEach(updateStickyNavState);
	});

	setTimeout(() => {
		document.querySelectorAll(STICKY_BAR_ACTIVE_SELECTOR).forEach(updateStickyNavState);
	}, 350);

	if (n >= 2 && n <= 5) {
		requestAnimationFrame(() => {
			scrollIngredientStepProgressIntoView({ instant: options.instant });
			requestAnimationFrame(() => {
				scrollIngredientStepProgressIntoView({ instant: true });
			});
		});
	}

	if (!options.skipPersist) {
		saveRecipeMakerState();
	}

	if (!options.fromHistory) {
		syncHistoryForScreen(n, options.historyMode || 'push');
	}

	trackRecipeMakerStep(n);
}

const stickyNavObservers = new WeakMap();
const STICKY_BAR_SELECTOR = '.recipe-maker-section .nav-row, .recipe-maker-section .reset-row';
const STICKY_BAR_ACTIVE_SELECTOR =
	'.recipe-maker-section .screen.active .nav-row, .recipe-maker-section .screen.active .reset-row';

function recipeMakerContentFitsViewport() {
	const activeScreen = document.querySelector('.recipe-maker-section .screen.active');

	if (!activeScreen) {
		return true;
	}

	const chromeTop = activeScreen.getBoundingClientRect().top;
	const availableHeight = window.innerHeight - Math.max(0, chromeTop);

	return activeScreen.offsetHeight <= availableHeight;
}

function updateStickyNavState(bar) {
	if (IS_THEME_EDITOR) {
		return;
	}

	const host = bar.closest('.nav-row-sticky');

	if (!host) {
		return;
	}

	const activeScreen = bar.closest('.screen');

	if (activeScreen && !activeScreen.classList.contains('active')) {
		host.classList.remove('is-stuck');
		host.style.height = '';

		return;
	}

	if (recipeMakerContentFitsViewport()) {
		host.classList.remove('is-stuck');
		host.style.height = '';

		return;
	}

	const hostRect = host.getBoundingClientRect();
	const shouldStick = hostRect.bottom > window.innerHeight;

	// Reserve the in-flow height before fixing — stuck layout can change (e.g. reset-row mobile).
	if (shouldStick && !host.classList.contains('is-stuck')) {
		host.style.height = `${bar.offsetHeight}px`;
	}

	host.classList.toggle('is-stuck', shouldStick);

	if (!shouldStick) {
		host.style.height = '';
	}
}

function wrapStickyNavRow(bar) {
	if (bar.closest('.nav-row-sticky')) {
		return;
	}

	const host = document.createElement('div');

	host.className = 'nav-row-sticky';
	bar.parentNode.insertBefore(host, bar);
	host.appendChild(bar);

	const observer = new ResizeObserver(() => {
		updateStickyNavState(bar);
	});

	observer.observe(activeScreenContentForNav(bar) || host);
	stickyNavObservers.set(bar, observer);
	updateStickyNavState(bar);
}

function activeScreenContentForNav(bar) {
	return bar.closest('.screen-content');
}

function initStickyNavRows() {
	if (!initStickyNavRows.listenersBound) {
		const onStickyNavUpdate = () => {
			document.querySelectorAll(STICKY_BAR_ACTIVE_SELECTOR).forEach(updateStickyNavState);
		};

		window.addEventListener('resize', onStickyNavUpdate, { passive: true });
		window.addEventListener('scroll', onStickyNavUpdate, { passive: true });
		initStickyNavRows.listenersBound = true;
	}

	document.querySelectorAll(STICKY_BAR_SELECTOR).forEach(wrapStickyNavRow);
	document.querySelectorAll(STICKY_BAR_SELECTOR).forEach(updateStickyNavState);
}

function getSkipNextConfigForScreen(screenNum) {
	const screenIndex = ORDER_CATS.findIndex((cat) => CATEGORY_ENTRY_SCREEN[cat] === screenNum);
	if (screenIndex < 0 || screenIndex >= ORDER_CATS.length - 1) return null;

	const fromCategory = ORDER_CATS[screenIndex];
	const skipCategory = ORDER_CATS[screenIndex + 1];
	const screen8 = window.recipeMakerScreen8 || {};
	const validation = screen8.ingredientValidation || {};
	const skipType = CATEGORY_TO_TYPE[skipCategory];
	const skipConfig = validation[skipType] || {};

	if (skipConfig.isMandatory) return null;

	const label = screen8.categories?.find((cat) => cat.key === skipCategory)?.label || skipCategory;

	return {
		fromCategory,
		skipCategory,
		targetScreen: CATEGORY_ENTRY_SCREEN[skipCategory] + 1,
		label,
	};
}

function buildSkipCategoryLabel(categoryLabel) {
	return formatToastMessage(I18N.skipCategoryTemplate || 'Skip __CATEGORY__', { category: categoryLabel });
}

function createSkipNextButton(config) {
	const btn = document.createElement('button');

	btn.type = 'button';
	btn.className = 'btn-ghost-nav align-center dk-skip-next-btn';
	btn.dataset.skipNextFrom = config.fromCategory;
	btn.textContent = buildSkipCategoryLabel(config.label);
	btn.addEventListener('click', () => {
		skipIngredientStep(config.skipCategory, config.targetScreen);
	});

	return btn;
}

function updateSkipNextButtons() {
	document.querySelectorAll('.dk-skip-next-btn').forEach((btn) => btn.remove());

	ORDER_CATS.forEach((category) => {
		const screenNum = CATEGORY_ENTRY_SCREEN[category];
		const config = getSkipNextConfigForScreen(screenNum);

		if (!config) return;

		const navActions = document.querySelector(`[data-nav-actions-for="${config.fromCategory}"]`);

		if (navActions) {
			navActions.insertBefore(createSkipNextButton(config), navActions.firstChild);
		}
	});
}

function updateProgress(screenNum) {
	let step = 1;
	if (screenNum >= 2 && screenNum <= 5) step = 1;
	else if (screenNum === 6) step = 2;
	else if (screenNum === 7) step = 3;
	else if (screenNum === 8) step = 4;

	document.querySelectorAll('.progress-step').forEach((el, i) => {
		el.classList.toggle('active', i + 1 <= step);
		el.classList.toggle('done', i + 1 < step);
	});

	const pct = ((step - 1) / 3) * 100;
	document.getElementById('progressFill').style.width = `${pct}%`;
}

function getCategoryLabelForPortions(cat) {
	const screen8 = window.recipeMakerScreen8 || {};
	return screen8.categories?.find((c) => c.key === cat)?.label || cat;
}

function isPortionCategoryLockedBySkip(cat) {
	const entry = CATEGORY_ENTRY_SCREEN[cat];
	if (entry == null) return false;
	return !!(state.skippedStepCategories[cat] && state.currentScreen > entry);
}

function setPortionsCategoryZeroAndRebalance(zeroCat) {
	const cur = {};
	ORDER_CATS.forEach((k) => {
		cur[k] = Math.max(0, Math.round(Number(state.proportions?.[k] ?? DEFAULT_PROPORTIONS[k] ?? 0)));
	});
	const freed = cur[zeroCat];
	cur[zeroCat] = 0;
	const others = ORDER_CATS.filter((k) => k !== zeroCat);
	let sumOthers = others.reduce((s, k) => s + cur[k], 0);

	if (sumOthers <= 0) {
		const per = Math.floor(100 / others.length);
		let rem = 100 - per * others.length;
		others.forEach((k) => {
			cur[k] = per + (rem > 0 ? 1 : 0);
			if (rem > 0) rem -= 1;
		});
		state.proportions = cur;
		return;
	}

	if (freed <= 0) {
		state.proportions = cur;
		return;
	}

	others.forEach((k) => {
		cur[k] += freed * (cur[k] / sumOthers);
	});
	others.forEach((k) => {
		cur[k] = Math.round(cur[k]);
	});
	cur[zeroCat] = 0;
	let sum = ORDER_CATS.reduce((s, k) => s + cur[k], 0);
	let drift = 100 - sum;
	const fixKey = others.reduce((best, k) => (cur[k] > cur[best] ? k : best), others[0]);
	cur[fixKey] = Math.max(0, cur[fixKey] + drift);
	state.proportions = cur;
}

function syncPortionInputsDisabledState() {
	ORDER_CATS.forEach((cat) => {
		const input = document.getElementById(`portionInput-${cat}`);
		if (!input) return;
		const locked = isPortionCategoryLockedBySkip(cat);
		input.disabled = locked;
		const label = getCategoryLabelForPortions(cat);
		input.title = locked ? String(I18N.portionSkippedLockedHint || '').replace('__CATEGORY__', label) : '';
	});
}

const BADGE_CONFIG = {
	healthy: { cls: 'badge-healthy' },
	caution: { cls: 'badge-caution' },
	moderation: { cls: 'badge-moderation' },
	small: { cls: 'badge-small' },
	cooked: { cls: 'badge-cooked' },
	spicy: { cls: 'badge-spicy' },
};

function renderIngredients(type) {
	const gridId = type === 'veggies' ? 'vegGrid' : type === 'organs' ? 'organGrid' : type === 'bones' ? 'boneGrid' : 'meatGrid';
	const grid = document.getElementById(gridId);
	if (!grid) return;
	grid.innerHTML = '';

	INGREDIENTS[type].forEach((item) => {
		const bc = BADGE_CONFIG[item.badge] || BADGE_CONFIG.healthy;
		const subtitle = String(item.subtitle || '').trim();
		const badgeMarkup = subtitle ? `<div class="ingredient-badge ${bc.cls}">${subtitle}</div>` : '';
		const isSelected = state.selected[type].includes(item.id);
		const card = document.createElement('button');
		card.type = 'button';
		card.className = `ingredient-card text-center position-relative flex-container flex-dir-column ${isSelected ? 'selected' : ''}`;
		card.dataset.id = item.id;
		card.dataset.type = type;
		card.setAttribute('aria-pressed', isSelected);
		const tcmBadge = item.tcm ? `<div class="ingredient-tcm tcm-${item.tcm}">${item.tcmLabel}</div>` : '';
		const visual = item.image
			? `<div class="ingredient-emoji"><img src="${item.image}" alt="${item.name}" loading="lazy" width="42" height="42"></div>`
			: item.emoji
				? `<div class="ingredient-emoji">${item.emoji}</div>`
				: item.icon
					? `<div class="ingredient-emoji">${item.icon}</div>`
					: ``;
		card.innerHTML = `
          ${visual}
          <div class="ingredient-name">${item.name}</div>
          <div class="ingredient-prep">${item.prep}</div>
          ${badgeMarkup}
          ${tcmBadge}
          ${item.desc ? `<div class="ingredient-desc text-left">${item.desc}</div>` : ''}
          <div class="check-icon flex-container align-center" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
        `;
		let ptrDown = null;
		card.addEventListener('pointerdown', (e) => {
			if (e.pointerType === 'mouse' && e.button !== 0) return;
			ptrDown = { x: e.clientX, y: e.clientY, id: e.pointerId };
		});
		card.addEventListener('pointerup', (e) => {
			if (!ptrDown || ptrDown.id !== e.pointerId) {
				ptrDown = null;
				return;
			}
			const dx = e.clientX - ptrDown.x;
			const dy = e.clientY - ptrDown.y;
			ptrDown = null;
			if (Math.hypot(dx, dy) > POINTER_ACTIVATION_MAX_MOVE_PX) return;
			toggleIngredient(type, item.id, card);
			card._rmSuppressIngredientClick = true;
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					card._rmSuppressIngredientClick = false;
				});
			});
		});
		card.addEventListener('pointercancel', () => {
			ptrDown = null;
		});
		card.addEventListener('click', (e) => {
			if (card._rmSuppressIngredientClick) {
				e.preventDefault();
				e.stopPropagation();
			}
		});
		card.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				toggleIngredient(type, item.id, card);
			}
		});
		grid.appendChild(card);
	});
}

function renderBodyConditionCards() {
	const grid = document.getElementById('bodyConditionGrid');
	if (!grid) return;
	grid.innerHTML = '';
	BODY_CONDITIONS_DATA.forEach((bc) => {
		const isActive = bc.value === (state.dog.bodyCondition || 'healthy-weight');
		const card = document.createElement('button');
		card.type = 'button';
		card.className = `condition-card text-center flex-container flex-dir-column ${isActive ? 'active' : ''}`;
		card.dataset.value = bc.value;
		card.dataset.adj = bc.adj;
		card.innerHTML = `
          <img src="${bc.image}" alt="${bc.label}" class="condition-img" loading="lazy" onerror="this.style.display='none'">
          <div class="condition-label">${bc.label}</div>
          <div class="condition-note">${bc.note}</div>
        `;
		card.addEventListener('click', () => selectCondition(card));
		grid.appendChild(card);
	});
	applyWeightGuideForValue(state.dog.bodyCondition || 'healthy-weight');
}

function applyWeightGuideForValue(value) {
	const guideBox = document.getElementById('weightGuideBox');
	if (!guideBox) return;
	const htmlFromTheme = WEIGHT_GUIDE_HTML[value];
	if (!htmlFromTheme || String(htmlFromTheme).trim() === '') {
		guideBox.innerHTML = '';
		guideBox.classList.add('is-hidden');
		guideBox.classList.remove('is-visible');
		return;
	}
	guideBox.innerHTML = htmlFromTheme;
	guideBox.classList.remove('is-hidden');
	guideBox.classList.add('is-visible');
}

function toggleIngredient(type, id, card) {
	if (isCategoryPortionZero(type)) {
		updateStepPortionUI(type);
		return;
	}

	const arr = state.selected[type];
	const maxMap = { meats: 8, veggies: 8, organs: 99, bones: 99 };
	const max = maxMap[type];

	if (arr.includes(id)) {
		state.selected[type] = arr.filter((x) => x !== id);
		card.classList.remove('selected');
		card.setAttribute('aria-pressed', false);
	} else {
		if (arr.length >= max) {
			showToast(I18N.maxSelected || '', 'info', { count: max });
			return;
		}
		state.selected[type].push(id);
		card.classList.add('selected');
		card.setAttribute('aria-pressed', true);
	}
	updateCounts();
	updateStepPortionUI(type);
	saveRecipeMakerState();
}

function updateCounts() {
	const m = document.getElementById('meatCount');
	const v = document.getElementById('vegCount');
	const o = document.getElementById('organCount');
	const b = document.getElementById('boneCount');
	if (m) m.textContent = state.selected.meats.length;
	if (v) v.textContent = state.selected.veggies.length;
	if (o) o.textContent = state.selected.organs.length;
	if (b) b.textContent = state.selected.bones.length;
}

function selectBlend(category, num) {
	if (isCategoryPortionZero(category)) {
		updateStepPortionUI(category);
		return;
	}

	const blendLines = BLENDS_BY_TYPE[category];
	let ids = [];

	if (Array.isArray(blendLines) && blendLines.length > 0) {
		const selectedLine = blendLines[num - 1] || '';
		const items = selectedLine
			.split(/[;,·]/)
			.map((item) => item.trim())
			.filter(Boolean);
		const availableIds = (INGREDIENTS[category] || []).map((item) => item.id);
		ids = items
			.map((item) =>
				item
					.toLowerCase()
					.replace(/[^a-z0-9]+/g, '-')
					.replace(/^-+|-+$/g, ''),
			)
			.filter((id) => availableIds.includes(id));
	}

	state.selected[category] = [...ids];

	const gridId = category === 'veggies' ? '#vegGrid' : category === 'organs' ? '#organGrid' : category === 'bones' ? '#boneGrid' : '#meatGrid';
	document.querySelectorAll(`${gridId} .ingredient-card`).forEach((card) => {
		const isIn = ids.includes(card.dataset.id);
		card.classList.toggle('selected', isIn);
		card.setAttribute('aria-pressed', isIn);
	});

	document.querySelectorAll(`.blend-card[data-category="${category}"]`).forEach((c, i) => {
		c.classList.toggle('active', i + 1 === num);
	});

	updateCounts();
	updateStepPortionUI(category);
	saveRecipeMakerState();
}

function selectToggle(btn, field) {
	const group = btn.closest('.toggle-group');
	group.querySelectorAll('.toggle-btn').forEach((b) => b.classList.remove('active'));
	btn.classList.add('active');

	const val = btn.dataset.value;
	if (field === 'gender') state.dog.gender = val;
	else if (field === 'weightUnit') state.dog.weightUnit = val;
	else if (field === 'age') state.dog.age = val;
	else if (field === 'newsletter') {
		state.recipe.newsletter = val;
		const emailRow = document.getElementById('newsletterEmailRow');
		if (emailRow) emailRow.style.display = val === 'yes' ? 'block' : 'none';
	}

	saveRecipeMakerState();
}

function selectFeedingExperience(btn) {
	const group = document.getElementById('feedingExperienceGroup');
	if (!group) return;

	group.querySelectorAll('.feeding-experience-option').forEach((option) => {
		option.classList.remove('active');
		option.setAttribute('aria-checked', 'false');
	});

	btn.classList.add('active');
	btn.setAttribute('aria-checked', 'true');
	state.dog.isFeedingRaw = btn.dataset.value === 'true';
	saveRecipeMakerState();
}

function syncFeedingExperienceToggle() {
	const group = document.getElementById('feedingExperienceGroup');
	if (!group) return;

	const val = state.dog.isFeedingRaw ? 'true' : 'false';

	group.querySelectorAll('.feeding-experience-option').forEach((btn) => {
		const isActive = btn.dataset.value === val;

		btn.classList.toggle('active', isActive);
		btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
	});
}

function selectCondition(btn) {
	document.querySelectorAll('.condition-card').forEach((c) => c.classList.remove('active'));
	btn.classList.add('active');
	state.dog.bodyCondition = btn.dataset.value;
	state.dog.bodyAdj = parseInt(btn.dataset.adj, 10);
	applyWeightGuideForValue(btn.dataset.value);
	saveRecipeMakerState();
}

function validateAndNext(fromScreen, toScreen, type) {
	const screen8 = window.recipeMakerScreen8 || {};
	const validation = screen8.ingredientValidation || {};
	const typeValidation = validation[type] || {};
	const selectedMap = {
		meat: state.selected.meats,
		veg: state.selected.veggies,
		organ: state.selected.organs,
		bone: state.selected.bones,
	};
	const selectedItems = Array.isArray(selectedMap[type]) ? selectedMap[type] : [];
	const hasSelection = selectedItems.length > 0;

	const categoryLabel = getCategoryLabelForPortions(TYPE_TO_CATEGORY[type] || '');

	if (!hasSelection && typeValidation.isMandatory) {
		if (typeValidation.requiredError && typeValidation.requiredError.trim() !== '') {
			showToast(typeValidation.requiredError, 'info', { category: categoryLabel });
		}
		return;
	}

	if (!hasSelection && !typeValidation.isMandatory) {
		if (typeValidation.optionalWarning && typeValidation.optionalWarning.trim() !== '') {
			showToast(typeValidation.optionalWarning, 'info', { category: categoryLabel });
		}
		const cat = TYPE_TO_CATEGORY[type];
		if (cat) setPortionsCategoryZeroAndRebalance(cat);
	}

	goToScreen(toScreen);
}

function skipIngredientStep(category, toScreen) {
	state.selected[category] = [];
	state.skippedStepCategories[category] = true;
	setPortionsCategoryZeroAndRebalance(category);

	const gridId = category === 'veggies' ? '#vegGrid' : category === 'organs' ? '#organGrid' : category === 'bones' ? '#boneGrid' : '#meatGrid';

	document.querySelectorAll(`${gridId} .ingredient-card`).forEach((card) => {
		card.classList.remove('selected');
		card.setAttribute('aria-pressed', false);
	});

	document.querySelectorAll(`.blend-card[data-category="${category}"]`).forEach((card) => {
		card.classList.remove('active');
	});

	updateCounts();
	updateStepPortionUI(category);
	goToScreen(toScreen);
}

function getCustomerDisplayName() {
	const firstName = String(state.recipe.customerFirstName || '').trim();
	const lastName = String(state.recipe.customerLastName || '').trim();

	return [firstName, lastName].filter(Boolean).join(' ');
}

function getCustomerNameApiFields() {
	const firstName = String(state.recipe.customerFirstName || '').trim();
	const lastName = String(state.recipe.customerLastName || '').trim();
	const fields = { customerFirstName: firstName };

	if (lastName) {
		fields.customerLastName = lastName;
	}

	return fields;
}

function validateDogDetails() {
	const customerFirstNameInput = document.getElementById('customerFirstName');
	const customerLastNameInput = document.getElementById('customerLastName');
	const customerFirstName = customerFirstNameInput ? customerFirstNameInput.value.trim() : '';
	const customerLastName = customerLastNameInput ? customerLastNameInput.value.trim() : '';

	if (!customerFirstName) {
		showToast(I18N.enterCustomerFirstName || '');
		if (customerFirstNameInput) customerFirstNameInput.focus();

		return;
	}

	const feedingExperienceBtn = document.querySelector('#feedingExperienceGroup .feeding-experience-option.active');
	if (!feedingExperienceBtn) {
		showToast(I18N.selectFeedingExperience || '');
		return;
	}

	const name = document.getElementById('dogName').value.trim();
	const weight = parseFloat(document.getElementById('dogWeight').value);
	if (!name) {
		showToast(I18N.enterDogName || '');
		return;
	}
	if (!weight || weight <= 0) {
		showToast(I18N.enterDogWeight || '');
		return;
	}

	state.recipe.customerFirstName = customerFirstName;
	state.recipe.customerLastName = customerLastName;
	state.dog.isFeedingRaw = feedingExperienceBtn.dataset.value === 'true';
	state.dog.name = name;
	state.dog.weight = weight;

	const rn = document.getElementById('recipeName');
	if (rn && !rn.value) rn.value = normalizeRecipeName('', name);

	saveRecipeMakerState();
	goToScreen(7);
}

function calculateRecipe() {
	let weightLbs = state.dog.weight;
	if (state.dog.weightUnit === 'kg') {
		weightLbs = state.dog.weight / 0.45359237;
	}

	let basegrams = weightLbs * 10;
	const adj = state.dog.bodyAdj / 100;
	const totalGrams = Math.round(basegrams * (1 + adj));

	const result = { total: totalGrams, meats: [], veggies: [], organs: [], bones: [] };
	const effectiveProportions = getEffectiveCategoryProportions();
	const activeCategories = Object.entries(effectiveProportions).filter(([cat, pct]) => {
		const selectedIds = state.selected[cat] || [];
		return selectedIds.length > 0 && Number(pct || 0) > 0;
	});

	if (activeCategories.length === 0) {
		return result;
	}

	activeCategories.forEach(([cat, pct]) => {
		const selectedIds = state.selected[cat] || [];
		const catGrams = totalGrams * (Number(pct || 0) / 100);

		const perIngredient = catGrams / selectedIds.length;
		const rounded = Math.round(perIngredient / 5) * 5;

		const allItems = INGREDIENTS[cat];
		selectedIds.forEach((id) => {
			const item = allItems.find((x) => x.id === id);
			if (item) {
				result[cat].push({
					...item,
					grams: rounded * state.days,
					oz: ((rounded * state.days) / 28.35).toFixed(1),
				});
			}
		});
	});

	return result;
}

function decodeHtmlEntities(value) {
	if (value == null || value === '') return '';

	const div = document.createElement('div');

	div.innerHTML = String(value);

	return div.textContent || '';
}

function getEffectiveCategoryProportions() {
	const baseMap = { ...DEFAULT_PROPORTIONS, ...(state.proportions || {}) };
	const keys = ['meats', 'veggies', 'organs', 'bones'];
	const result = {};
	let inactivePercentTotal = 0;
	let activeCount = 0;

	keys.forEach((cat) => {
		const selectedIds = state.selected[cat] || [];
		const pct = Number(baseMap[cat] || 0);
		const hasSelection = selectedIds.length > 0;

		if (hasSelection) activeCount += 1;
		else inactivePercentTotal += pct;

		result[cat] = pct;
	});

	if (!activeCount) {
		keys.forEach((cat) => {
			result[cat] = 0;
		});
		return result;
	}

	const redistributedShare = inactivePercentTotal / activeCount;

	keys.forEach((cat) => {
		const selectedIds = state.selected[cat] || [];

		if (selectedIds.length > 0) result[cat] = Number(result[cat] || 0) + redistributedShare;
		else result[cat] = 0;
	});

	return result;
}

function normalizeRecipeName(value, fallbackName) {
	const raw = String(value || '').trim();

	if (raw) return decodeHtmlEntities(raw);

	const template = String(I18N.defaultRecipeNameTemplate || '').replace('__NAME__', fallbackName || '');

	return decodeHtmlEntities(template);
}

function getMealsPerDayByAge(ageValue) {
	return ageValue === 'puppy' ? '2-3' : '1-2';
}

/** Shopify image fields often use protocol-relative URLs; email/API consumers need absolute https. */
function ensureHttpsImageUrl(url) {
	const s = String(url || '').trim();

	if (!s) return '';

	if (s.startsWith('//')) return `https:${s}`;

	if (s.startsWith('http://')) return `https://${s.slice(7)}`;

	return s;
}

function ensureAbsoluteHttpsPageUrl(url) {
	let s = String(url || '').trim();

	if (!s) return '';

	if (s.startsWith('/')) s = `${window.location.origin}${s}`;

	return ensureHttpsImageUrl(s);
}

function getDosageColumnIndexForWeightLbs(weightLbs) {
	const table = document.querySelector('.dosage-table');

	if (!table) return -1;

	const headers = Array.from(table.querySelectorAll('thead th[data-range-start][data-range-end]'));

	return headers.findIndex((th) => {
		const start = Number(th.getAttribute('data-range-start'));
		const end = Number(th.getAttribute('data-range-end'));

		return weightLbs > start && weightLbs <= end;
	});
}

/** Same dosage strings as the PDF / print supplement cards (from dosage table column for dog weight). */
function getDosageTextsByProductHandleForWeightLbs(weightLbs) {
	const table = document.querySelector('.dosage-table');

	if (!table || !weightLbs) return {};

	const targetColIndex = getDosageColumnIndexForWeightLbs(weightLbs);

	if (targetColIndex < 0) return {};

	const servingsByHandle = {};

	table.querySelectorAll('.dosage-table tbody tr[data-product-handle]').forEach((row) => {
		const handle = row.getAttribute('data-product-handle');
		const cells = row.querySelectorAll('td');
		const dosageCell = cells[targetColIndex + 1];
		const dosageSpan = dosageCell ? dosageCell.querySelector('[data-dosage-text]') : null;
		const dosageText = (dosageSpan ? dosageSpan.textContent : dosageCell?.textContent || '—').trim() || '—';

		if (handle) servingsByHandle[handle] = dosageText;
	});

	return servingsByHandle;
}

function getDosageFootnotePlainText() {
	const el = document.querySelector('.dosage-table-wrap .dosage-footnote') || document.querySelector('.print-supp-summary__footnote');

	if (!el) return '';

	return el.textContent.replace(/\s+/g, ' ').trim();
}

function buildHowToServeEmailPayload() {
	const root = document.getElementById('suppHowto');

	if (!root) return { title: '', items: [] };

	const titleEl = root.querySelector('.supp-howto-title');
	const title = titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : '';
	const items = [];

	root.querySelectorAll('.howto-card').forEach((card) => {
		const titleNode = card.querySelector('.howto-text strong');
		const bodyNode = card.querySelector('.howto-text div') || card.querySelector('.howto-text p');
		const iconImg = card.querySelector('.howto-icon-image');
		const itemTitle = titleNode ? titleNode.textContent.trim() : '';
		const body = bodyNode ? bodyNode.textContent.replace(/\s+/g, ' ').trim() : '';

		if (!itemTitle && !body) return;

		items.push({
			title: itemTitle,
			body,
			...(iconImg && iconImg.src ? { iconUrl: ensureHttpsImageUrl(iconImg.src) } : {}),
		});
	});

	return { title, items };
}

function buildEssentialSupplementsEmailPayload() {
	const description = getDosageFootnotePlainText();
	const howToServe = buildHowToServeEmailPayload();
	const products = [];
	const weightValue = Number(state.dog.weight);

	if (!weightValue) return { products, description, howToServe };

	const weightLbs = state.dog.weightUnit === 'kg' ? weightValue / 0.45359237 : weightValue;
	const dosageByHandle = getDosageTextsByProductHandleForWeightLbs(weightLbs);

	document.querySelectorAll('.print-supp-card[data-product-handle]').forEach((card) => {
		const handle = card.getAttribute('data-product-handle');

		if (!handle) return;

		const titleEl = card.querySelector('.print-supp-card__title');
		const title = (titleEl ? titleEl.textContent : '').trim() || handle;
		const rawUrl = card.getAttribute('data-product-url') || '';
		const url = ensureAbsoluteHttpsPageUrl(rawUrl);
		const imgEl = card.querySelector('.print-supp-card__img');
		const imageUrl = imgEl && imgEl.src ? ensureHttpsImageUrl(imgEl.src) : '';

		products.push({
			title,
			url,
			dosage: dosageByHandle[handle] || '—',
			...(imageUrl ? { imageUrl } : {}),
		});
	});

	return { products, description, howToServe };
}

function buildRecipeEmailApiPayload(customerEmail) {
	const screen8 = window.recipeMakerScreen8 || {};
	const catLabelByKey = {};

	(screen8.categories || []).forEach((c) => {
		catLabelByKey[c.key] = c.label || c.key;
	});

	const rec = calculateRecipe();
	const dog = state.dog;
	const days = state.days;
	const order = ['meats', 'veggies', 'organs', 'bones'];
	const ingredients = [];

	order.forEach((key) => {
		const sectionLabel = catLabelByKey[key] || key;

		(rec[key] || []).forEach((item) => {
			const imageUrl = ensureHttpsImageUrl(item.image);

			ingredients.push({
				section: sectionLabel,
				name: item.name || '',
				grams: Number(item.grams) || 0,
				prep: String(item.prep || '').trim(),
				imageUrl: imageUrl || '',
			});
		});
	});

	const rawWeight = Number(dog.weight) || 0;
	const roundedWeight = Math.round(rawWeight * 100) / 100;
	const conditionRow = BODY_CONDITIONS_DATA.find((x) => x.value === dog.bodyCondition);
	const conditionLabel = conditionRow?.label || String(dog.bodyCondition || '').replace(/-/g, ' ');
	const subscribed = state.recipe.newsletter === 'yes';
	const dogAge =
		String(dog.age || 'adult')
			.trim()
			.toLowerCase() === 'puppy'
			? 'puppy'
			: 'adult';
	const recipeName = normalizeRecipeName(state.recipe.name, dog.name || '');

	const dogDescription = String(state.recipe.story || '').trim();
	const dogDetails = {
		name: dog.name || '',
		weight: roundedWeight,
		weightUnit: dog.weightUnit === 'kg' ? 'kg' : 'lbs',
		condition: conditionLabel,
		age: dogAge,
	};
	if (dogDescription && dogDescription.trim() !== '') dogDetails.description = dogDescription;

	const supplements = buildEssentialSupplementsEmailPayload();
	const categoryNotes = {};

	(screen8.categories || []).forEach((cat) => {
		if (!cat || !cat.key) return;

		const note = String(cat.note || '').trim();

		if (note) categoryNotes[cat.key] = note;
	});

	return {
		customerEmail: btoa(customerEmail),
		...getCustomerNameApiFields(),
		supplements,
		recipeJson: {
			summary: {
				dayCount: days,
				totalWeightGrams: rec.total * days,
				mealsPerDay: getMealsPerDayByAge(dogAge),
			},
			ingredients,
			categoryNotes,
		},
		dogDetails,
		recipeName,
		subscribed,
		is_feeding_raw: !!state.dog.isFeedingRaw,
	};
}

async function postRecipeEmailToApp(customerEmail) {
	if (IS_THEME_EDITOR) return;

	const consent = document.getElementById('newsletterConsent');
	const sendToggleOn = !!(consent && consent.checked);
	const trimmed = String(customerEmail || '').trim();
	const emailIsValid = !!(trimmed && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed));

	if (!sendToggleOn || !emailIsValid) return;

	const path = RECIPE_EMAIL_CONFIG.apiPath;
	const payload = buildRecipeEmailApiPayload(trimmed);

	try {
		const requestConfig = {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify(payload),
		};

		// console.log('[Recipe Maker] Proxy request', {
		//   url: path,
		//   ...requestConfig,
		//   body: payload,
		// });

		const response = await fetch(path, requestConfig);
		const contentType = response.headers.get('content-type') || '';
		let parsedResponse;

		if (contentType.includes('application/json')) parsedResponse = await response.json();
		else parsedResponse = await response.text();

		// console.log('[Recipe Maker] Proxy response', {
		//   ok: response.ok,
		//   status: response.status,
		//   statusText: response.statusText,
		//   body: parsedResponse,
		// });

		if (!response.ok) {
			const errMsg = typeof parsedResponse === 'string' ? parsedResponse : JSON.stringify(parsedResponse);

			throw new Error(errMsg || String(response.status));
		}

		showToast(I18N.recipeSentInbox || 'Recipe sent.', 'success', { email: trimmed || state.recipe.email });
	} catch (err) {
		console.error('Recipe maker email API', err);
		showToast('Could not send recipe by email. Please try again.', 'info');
	}
}

function buildAndShowRecipe() {
	state.recipe.name = normalizeRecipeName(document.getElementById('recipeName').value, state.dog.name || '');
	state.recipe.story = document.getElementById('dogStory').value.trim();

	const emailInput = document.getElementById('newsletterEmail');
	const consentCheck = document.getElementById('newsletterConsent');
	const newsletterOptInCheck = document.getElementById('newsletterOptIn');
	const isEmailRequired = !!(consentCheck && consentCheck.checked);

	if (emailInput) state.recipe.email = emailInput.value.trim();
	state.recipe.newsletter = newsletterOptInCheck && newsletterOptInCheck.checked ? 'yes' : 'no';

	if (isEmailRequired) {
		const emailIsValid = !!(state.recipe.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.recipe.email));
		if (!emailIsValid) {
			showToast(I18N.emailInvalid || '');
			if (emailInput) emailInput.focus();
			return;
		}
	}

	if (!IS_THEME_EDITOR && isEmailRequired && state.recipe.email) {
		setTimeout(() => {
			void postRecipeEmailToApp(state.recipe.email);
		}, 1200);
	}

	triggerRecipeMakerConvertConversion(CONVERT_RECIPE_MAKER_FINISH_CONVERSION);
	recipeGenerated = true;
	goToScreen(8);
	renderRecipe();
}

function getPortionEditorConfig() {
	const screen8 = window.recipeMakerScreen8 || {};
	return screen8.portionEditor || {};
}

function openPortionEditor(category) {
	const cfg = getPortionEditorConfig();
	if (!cfg.enabled) return;
	const modal = document.getElementById('portionsModal');
	if (!modal) return;
	const titleEl = document.getElementById('portionsModalTitle');
	const introEl = document.getElementById('portionsModalIntro');
	const noteEl = document.getElementById('portionsModalNote');
	if (titleEl) titleEl.textContent = cfg.modalTitle || I18N.portionsModalTitleFallback || '';
	if (introEl) introEl.textContent = cfg.modalIntro || '';
	if (noteEl) noteEl.textContent = cfg.modalNote || '';
	['meats', 'veggies', 'organs', 'bones'].forEach((cat) => {
		const input = document.getElementById(`portionInput-${cat}`);
		if (input) input.value = Number(state.proportions?.[cat] ?? DEFAULT_PROPORTIONS[cat] ?? 0);
	});
	syncPortionInputsDisabledState();
	modal.style.display = 'flex';
	if (category) state.dismissedZeroPortion[category] = false;
}

function closePortionModal(e) {
	if (e && e.target && e.target.id !== 'portionsModal') return;
	const modal = document.getElementById('portionsModal');
	if (modal) modal.style.display = 'none';
	ORDER_CATS.forEach((cat) => {
		const input = document.getElementById(`portionInput-${cat}`);
		if (input) {
			input.disabled = false;
			input.removeAttribute('title');
		}
	});
}

function resetPortionsToDefaults() {
	state.proportions = { ...DEFAULT_PROPORTIONS };
	['meats', 'veggies', 'organs', 'bones'].forEach((cat) => {
		const input = document.getElementById(`portionInput-${cat}`);
		if (input) input.value = Number(DEFAULT_PROPORTIONS[cat] || 0);
	});
	syncPortionInputsDisabledState();
}

function savePortions() {
	const cfg = getPortionEditorConfig();
	const next = {};
	let total = 0;
	['meats', 'veggies', 'organs', 'bones'].forEach((cat) => {
		const input = document.getElementById(`portionInput-${cat}`);
		const value = Math.max(0, Math.min(100, Number(input?.value || 0)));
		next[cat] = value;
		total += value;
	});
	if (Math.round(total) !== 100) {
		showToast(cfg.sumError || I18N.portionsSumError || '');
		return;
	}
	const screen8 = window.recipeMakerScreen8 || {};
	const mandatoryByCategory = {
		meats: !!screen8.ingredientValidation?.meat?.isMandatory,
		veggies: !!screen8.ingredientValidation?.veg?.isMandatory,
		organs: !!screen8.ingredientValidation?.organ?.isMandatory,
		bones: !!screen8.ingredientValidation?.bone?.isMandatory,
	};
	const categoryLabelByKey = {
		meats: screen8.categories?.find((c) => c.key === 'meats')?.label || 'Meats',
		veggies: screen8.categories?.find((c) => c.key === 'veggies')?.label || 'Vegetables',
		organs: screen8.categories?.find((c) => c.key === 'organs')?.label || 'Organs',
		bones: screen8.categories?.find((c) => c.key === 'bones')?.label || 'Raw bones',
	};
	const zeroMandatoryCategory = Object.keys(mandatoryByCategory).find(
		(categoryKey) => mandatoryByCategory[categoryKey] && Number(next[categoryKey]) <= 0,
	);
	if (zeroMandatoryCategory) {
		showToast(cfg.mandatoryZeroError || I18N.portionMandatoryZeroFallback || '', 'info', {
			category: categoryLabelByKey[zeroMandatoryCategory] || '',
		});
		return;
	}
	const blocked = ORDER_CATS.find((cat) => isPortionCategoryLockedBySkip(cat) && Number(next[cat]) > 0);
	if (blocked) {
		const label = categoryLabelByKey[blocked] || getCategoryLabelForPortions(blocked);
		showToast(I18N.portionSkippedLocked || '', 'info', { category: label });
		return;
	}
	state.proportions = next;
	state.dismissedZeroPortion = { meats: false, veggies: false, organs: false, bones: false };
	closePortionModal();
	['meats', 'veggies', 'organs', 'bones'].forEach((cat) => updateStepPortionUI(cat));
	if (state.currentScreen === 8) renderRecipe();
	saveRecipeMakerState();
}

function dismissPortionOverlay(category) {
	state.dismissedZeroPortion[category] = true;
	updateStepPortionUI(category);
	saveRecipeMakerState();
}

function continueFromZeroOverlay(category, nextScreen) {
	dismissPortionOverlay(category);
	goToScreen(nextScreen);
}

function updateStepPortionUI(category) {
	const cfg = getPortionEditorConfig();
	const showEditor = !!cfg.enabled;
	const btn = document.getElementById(`portionEditorBtn-${category}`);
	if (btn) btn.style.display = showEditor ? 'inline-flex' : 'none';
	const overlay = document.getElementById(`portionOverlay-${category}`);
	const overlayText = document.getElementById(`portionOverlayText-${category}`);
	if (!overlay) return;
	if (!showEditor) {
		overlay.style.display = 'none';
		return;
	}
	const pct = Number(state.proportions?.[category] ?? DEFAULT_PROPORTIONS[category] ?? 0);
	const needsOverlay = pct <= 0 && !state.dismissedZeroPortion[category];
	if (!needsOverlay) {
		overlay.style.display = 'none';
		return;
	}
	const perCatCopy = {
		meats: cfg.overlayMeats,
		veggies: cfg.overlayVeggies,
		organs: cfg.overlayOrgans,
		bones: cfg.overlayBones,
	};
	if (overlayText) overlayText.textContent = perCatCopy[category] || I18N.portionZeroOverlayFallback || '';
	overlay.style.display = 'flex';
}

function isCategoryPortionZero(category) {
	const pct = Number(state.proportions?.[category] ?? DEFAULT_PROPORTIONS[category] ?? 0);
	return pct <= 0;
}

function escapeHtml(str) {
	if (str == null || str === '') return '';
	const d = document.createElement('div');
	d.textContent = str;
	return d.innerHTML;
}

function decodeToastText(msg) {
	if (msg == null || msg === '') return '';

	let text = String(msg);
	const textarea = document.createElement('textarea');

	textarea.innerHTML = text;
	if (textarea.value !== text) {
		text = textarea.value;
	}

	return text
		.replace(/&#39;/gi, "'")
		.replace(/&apos;/gi, "'")
		.replace(/&#x27;/gi, "'")
		.replace(/&quot;/gi, '"')
		.replace(/&amp;/gi, '&')
		.replace(/\u2019|\u2018/g, "'");
}

function getToastVars(overrides = {}) {
	return {
		category: '',
		count: '',
		email: state.recipe?.email || '',
		name: state.dog?.name || I18N.defaultDogName || '',
		meals: '',
		weight: `${state.dog?.weight || ''} ${state.dog?.weightUnit || ''}`.trim(),
		percent: '',
		...overrides,
	};
}

function replaceAllLiteral(str, search, replacement) {
	if (!search || !str.includes(search)) return str;

	return str.split(search).join(String(replacement ?? ''));
}

function formatToastMessage(template, overrides = {}) {
	let msg = decodeToastText(template);
	const vars = getToastVars(overrides);

	Object.entries(vars).forEach(([key, value]) => {
		const safe = String(value ?? '');
		const upper = key.toUpperCase();

		msg = replaceAllLiteral(msg, `__${upper}__`, safe);
		msg = replaceAllLiteral(msg, `{{${key}}}`, safe);
		msg = replaceAllLiteral(msg, `{{ ${key} }}`, safe);
		msg = replaceAllLiteral(msg, `[[${key}]]`, safe);
	});

	return msg;
}

function getRecipeMainMarkupForPdfContentSlot() {
	const hr = document.getElementById('recipeHeader');
	const main = document.querySelector('.print-pdf-recipe-main');
	const ds = main ? main.querySelector('.days-scaler') : null;
	const rg = document.getElementById('recipeGrid');
	const ns = document.getElementById('recipeNoteStack');
	const supp = document.getElementById('printSuppSummary');
	const howto = document.getElementById('suppHowto');

	if (!hr || !rg) return '';

	const box = document.createElement('div');

	box.className = 'print-pdf-intro__embedded-recipe';
	box.appendChild(hr.cloneNode(true));

	if (ds) box.appendChild(ds.cloneNode(true));

	box.appendChild(rg.cloneNode(true));

	if (ns) box.appendChild(ns.cloneNode(true));

	if (supp) box.appendChild(supp.cloneNode(true));

	if (howto) box.appendChild(howto.cloneNode(true));

	box.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));

	return box.innerHTML;
}

function hydratePrintPdfIntroPlaceholders() {
	const wrap = document.querySelector('.print-recipe-pdf-intro .print-pdf-intro__inner');
	const srcEl = document.getElementById('recipeMakerPdfIntroSource');

	if (!wrap || !srcEl) return;

	let tplHtml = srcEl.innerHTML;

	if (!tplHtml || !String(tplHtml).trim()) return;

	if (tplHtml.includes('[[content]]')) {
		const body = getRecipeMainMarkupForPdfContentSlot();
		tplHtml = tplHtml.split('[[content]]').join(`<hr class="print-pdf-content-hr" />${body}`);
	}

	const customer = escapeHtml(getCustomerDisplayName());
	const dog = escapeHtml(state.dog.name || '');
	let html = tplHtml;

	html = html.split('[[customerName]]').join(customer);
	html = html.split('[[dogName]]').join(dog);
	wrap.innerHTML = html;
}

function recipeItemVisualMarkup(item) {
	const name = item.name || '';
	if (item.image) {
		return `<div class="item-emoji"><img src="${escapeHtml(item.image)}" alt="${escapeHtml(name)}" loading="lazy" width="42" height="42"></div>`;
	}
	if (item.emoji) return `<div class="item-emoji">${item.emoji}</div>`;
	if (item.icon) return `<div class="item-emoji">${item.icon}</div>`;
	return '<div class="item-emoji item-emoji--empty" aria-hidden="true"></div>';
}

function renderRecipe() {
	const rec = calculateRecipe();
	const dog = state.dog;
	const days = state.days;
	const effectiveProportions = getEffectiveCategoryProportions();
	const screen8 = window.recipeMakerScreen8 || {};
	const titleTemplate = (screen8.titleTemplate || '').trim();
	const recipeTitleHtml = titleTemplate ? titleTemplate.replace(/\[\[name\]\]/g, escapeHtml(dog.name || '')) : escapeHtml(state.recipe.name || '');

	const titleEl = document.getElementById('recipeHeaderTitle');
	const cardsEl = document.getElementById('recipeInfoCards');
	const daysLabel = days > 1 ? I18N.daysTotalPlural || '' : I18N.daysTotalSingular || '';

	if (titleEl) {
		titleEl.innerHTML = `
        <h2 class="recipe-title-heading">${recipeTitleHtml}</h2>
        <p class="recipe-meta">
          For <strong>${escapeHtml(dog.name)}</strong> · ${dog.weight} ${dog.weightUnit} · ${dog.bodyCondition.replace(/-/g, ' ')} ·
          ${dog.age === 'puppy' ? I18N.ageUnder6Months || '' : I18N.ageOver6Months || ''}
        </p>
      `;
	}

	if (cardsEl) {
		cardsEl.innerHTML = `
        <div class="recipe-info-card recipe-info-card--meals">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          <div class="recipe-info-card__content">
            ${String(I18N.feedMealsPerDay || '').replace('__MEALS__', dog.age === 'puppy' ? '2–3' : '1–2')} · ${I18N.totalDaily || ''}: <strong>${rec.total}g (${(rec.total / 28.35).toFixed(1)} oz)</strong>
          </div>
        </div>
        <div class="recipe-info-card recipe-info-card--total">
          <div class="recipe-info-card__content">
            <div class="recipe-info-card__value">${rec.total * days}g <span class="recipe-info-card__value-sub">(${((rec.total * days) / 28.35).toFixed(1)} oz)</span></div>
            <div class="recipe-info-card__label">${days} ${daysLabel}</div>
          </div>
        </div>
      `;
	}

	const grid = document.getElementById('recipeGrid');
	grid.innerHTML = '';
	const categories = Array.isArray(screen8.categories) ? screen8.categories : [];
	const recipeColClassByKey = { meats: 'meat', veggies: 'veg', organs: 'organ', bones: 'bone' };

	categories.forEach((cat) => {
		const items = rec[cat.key];
		const col = document.createElement('div');
		col.className = `recipe-col recipe-col-${recipeColClassByKey[cat.key] || cat.key}`;
		if (cat.colorHex) col.style.setProperty('--recipe-col-accent', cat.colorHex);
		const categoryIcon = cat.image
			? `<img src="${escapeHtml(cat.image)}" alt="${escapeHtml(cat.label || '')}" class="cat-icon-image" loading="lazy" width="28" height="28">`
			: cat.emoji
				? `<span class="cat-icon-emoji">${cat.emoji}</span>`
				: '';
		col.innerHTML = `
          <div class="recipe-col-header">
            <span class="cat-icon">${categoryIcon}</span>
            <div>
              <div class="cat-label">${escapeHtml(cat.label || '')}</div>
              <div class="cat-pct">${String(I18N.categoryPercentOfMeal || '').replace(
					'__PERCENT__',
					Number(effectiveProportions?.[cat.key] ?? 0)
						.toFixed(1)
						.replace(/\.0$/, ''),
				)}</div>
            </div>
          </div>
          <div class="recipe-items">
            ${
				items.length > 0
					? items
							.map(
								(item) => `
                <div class="recipe-item">
                  ${recipeItemVisualMarkup(item)}
                  <div class="item-details">
                    <div class="item-name">${escapeHtml(item.name)}</div>
                    <div class="item-prep">${escapeHtml(item.prep || '')}</div>
                  </div>
                  <div class="item-amount text-right">
                    <div class="item-grams">${item.grams}g</div>
                    <div class="item-oz">${item.oz} oz</div>
                  </div>
                </div>
              `,
							)
							.join('')
					: `<div class="recipe-empty text-center">${String(I18N.recipeEmpty || '').replace('__CATEGORY__', (cat.label || '').toLowerCase())}</div>`
			}
          </div>
          <div class="cat-note">${escapeHtml(cat.note || '')}</div>
        `;
		grid.appendChild(col);
	});

	renderSupplements();
	hydratePrintPdfIntroPlaceholders();
}

function renderSocialShareButtons() {
	const screen8 = window.recipeMakerScreen8 || {};
	const shareCfg = screen8.share || {};
	const url = encodeRecipeToURL();
	const dogName = state.dog.name || 'my dog';
	const text = String(I18N.shareTextTemplate || '').replace('__NAME__', dogName);
	const hashtags = 'RawDogFood,DrDobias,HealthyDog,NaturalPetHealth';

	const shareName = document.getElementById('shareDogName');
	if (shareName) shareName.textContent = String(I18N.shareNameTemplate || '').replace('__NAME__', dogName);

	const shareInput = document.getElementById('shareUrlInput');
	if (shareInput) shareInput.value = url;

	const platforms = [
		{
			name: I18N.socialFacebook || '',
			enabled: !!shareCfg.showFacebook,
			color: '#1877F2',
			icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>`,
			url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
		},
		{
			name: I18N.socialTwitter || '',
			enabled: !!shareCfg.showTwitter,
			color: '#000000',
			icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.858L1.645 2.25H8.34l4.265 5.636L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/></svg>`,
			url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}&hashtags=${hashtags}`,
		},
	];

	const container = document.getElementById('socialBtns');
	if (!container) return;
	container.innerHTML = '';
	platforms
		.filter((p) => p.enabled)
		.forEach((p) => {
			const btn = document.createElement('a');
			btn.href = p.url;
			btn.target = '_blank';
			btn.rel = 'noopener noreferrer';
			btn.className = 'social-btn';
			btn.style.setProperty('--social-color', p.color);
			btn.innerHTML = `${p.icon}<span>${p.name}</span>`;
			container.appendChild(btn);
		});
}

function applyDogNamePlaceholders() {
	const dogName = state.dog.name || I18N.defaultDogName || '';
	const safeName = escapeHtml(dogName);
	const weightLabel = `${state.dog.weight || ''} ${state.dog.weightUnit || ''}`.trim();

	const replaceName = (template) => String(template || '').replace(/\[\[name\]\]/g, safeName);

	const discountBanner = document.getElementById('discountBannerTitle');
	if (discountBanner) {
		const template = discountBanner.getAttribute('data-template') || '';
		if (template) discountBanner.innerHTML = replaceName(template);
	}

	const finishBtn = document.getElementById('finishRecipeButton');
	if (finishBtn) {
		const template = finishBtn.getAttribute('data-template') || '';
		if (template) finishBtn.textContent = template.replace(/\[\[name\]\]/g, dogName);
	}

	const suppTitle = document.getElementById('supplementsTitleText');
	if (suppTitle) {
		const titleTemplate = suppTitle.getAttribute('data-template') || '';
		if (titleTemplate) suppTitle.textContent = titleTemplate.replace(/\[\[name\]\]/g, dogName);
	}

	const printSubtitle = document.getElementById('printSuppSummarySubtitle');
	if (printSubtitle) {
		const subtitleTemplate = printSubtitle.getAttribute('data-template') || '';
		if (subtitleTemplate) {
			printSubtitle.textContent = subtitleTemplate.replace(/\[\[name\]\]/g, dogName).replace(/\[\[weight\]\]/g, weightLabel);
		}
	}

	const shareTitle = document.getElementById('shareTitleText');
	if (shareTitle) {
		const shareTemplate = shareTitle.getAttribute('data-template') || '';
		if (shareTemplate) shareTitle.textContent = shareTemplate.replace(/\[\[name\]\]/g, dogName);
	}
}

function renderSupplements() {
	applyDogNamePlaceholders();
	updateSupplementServingsForWeight();
	renderSocialShareButtons();
}

function updateSupplementServingsForWeight() {
	const weightValue = Number(state.dog.weight);

	if (!weightValue) return;

	const weightLbs = state.dog.weightUnit === 'kg' ? weightValue / 0.45359237 : weightValue;
	const servingsByHandle = getDosageTextsByProductHandleForWeightLbs(weightLbs);

	document.querySelectorAll('.print-supp-card[data-product-handle]').forEach((card) => {
		const handle = card.getAttribute('data-product-handle');
		const target = card.querySelector('[data-serving-text]');

		if (!target || !handle) return;

		target.textContent = servingsByHandle[handle] || '—';
	});
}

function syncDayCounters() {
	['daysCount', 'daysCountStep2'].forEach((counterId) => {
		const counter = document.getElementById(counterId);

		if (counter) counter.textContent = state.days;
	});
}

function adjustDays(delta) {
	state.days = Math.max(1, Math.min(14, state.days + delta));
	syncDayCounters();
	renderRecipe();
	saveRecipeMakerState();
}

function printRecipe() {
	window.print();
}

// function emailRecipe() {
//   document.getElementById('emailModal').style.display = 'flex';
//   const input = document.getElementById('emailInputModal');
//   if (state.recipe.email && !input.value) input.value = state.recipe.email;
//   input.focus();
// }

// function closeEmailModal(e) {
//   if (e.target.id === 'emailModal') document.getElementById('emailModal').style.display = 'none';
// }

// function sendRecipeEmail() {
//   const email = document.getElementById('emailInputModal').value.trim();
//   if (!email || !email.includes('@')) {
//     showToast(I18N.emailInvalid || '');
//     return;
//   }
//   document.getElementById('emailModal').style.display = 'none';
//   showToast(String(I18N.recipeSentInbox || '').replace('__EMAIL__', email), 'success');
// }

function encodeRecipeToURL() {
	const payload = {
		m: state.selected.meats,
		v: state.selected.veggies,
		o: state.selected.organs,
		b: state.selected.bones,
		n: state.dog.name,
		cfn: state.recipe.customerFirstName,
		cln: state.recipe.customerLastName,
		g: state.dog.gender,
		w: state.dog.weight,
		u: state.dog.weightUnit,
		a: state.dog.age,
		c: state.dog.bodyCondition,
		ifr: state.dog.isFeedingRaw,
		r: state.recipe.name,
	};
	const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
	const base = window.location.origin + window.location.pathname;
	return `${base}?recipe=${encoded}`;
}

function decodeRecipeFromURL() {
	const params = new URLSearchParams(window.location.search);
	const encoded = params.get('recipe');
	if (!encoded) return false;
	try {
		const payload = JSON.parse(decodeURIComponent(escape(atob(encoded))));
		state.selected.meats = payload.m || [];
		state.selected.veggies = payload.v || [];
		state.selected.organs = payload.o || [];
		state.selected.bones = payload.b || [];
		state.dog.name = payload.n || '';
		if (typeof payload.cfn === 'string' || typeof payload.cln === 'string') {
			state.recipe.customerFirstName = typeof payload.cfn === 'string' ? payload.cfn : '';
			state.recipe.customerLastName = typeof payload.cln === 'string' ? payload.cln : '';
		} else if (typeof payload.cn === 'string' && payload.cn.trim()) {
			const legacyParts = payload.cn.trim().split(/\s+/);

			if (legacyParts.length > 1) {
				state.recipe.customerFirstName = legacyParts.slice(0, -1).join(' ');
				state.recipe.customerLastName = legacyParts[legacyParts.length - 1];
			} else {
				state.recipe.customerFirstName = legacyParts[0];
				state.recipe.customerLastName = '';
			}
		} else {
			state.recipe.customerFirstName = '';
			state.recipe.customerLastName = '';
		}

		state.dog.gender = payload.g || 'Male';
		state.dog.weight = payload.w || null;
		state.dog.weightUnit = payload.u || 'lbs';
		state.dog.age = payload.a || 'adult';
		state.dog.bodyCondition = payload.c || 'healthy-weight';
		state.dog.bodyAdj = BODY_CONDITIONS[payload.c] || 0;
		state.dog.isFeedingRaw = payload.ifr === true;
		state.recipe.name = payload.r || '';
		const customerFirstNameEl = document.getElementById('customerFirstName');
		const customerLastNameEl = document.getElementById('customerLastName');
		if (customerFirstNameEl) customerFirstNameEl.value = state.recipe.customerFirstName || '';
		if (customerLastNameEl) customerLastNameEl.value = state.recipe.customerLastName || '';
		syncFeedingExperienceToggle();

		return true;
	} catch (e) {
		return false;
	}
}

function copyShareLink() {
	const url = encodeRecipeToURL();
	navigator.clipboard
		.writeText(url)
		.then(() => {
			showToast(I18N.linkCopiedShare || '', 'success');
		})
		.catch(() => {
			prompt(I18N.sharePrompt || '', url);
		});
}

function shareRecipe() {
	const url = encodeRecipeToURL();
	const text = String(I18N.shareTextTemplate || '').replace('__NAME__', state.dog.name || 'my dog');
	if (navigator.share) {
		navigator.share({ title: state.recipe.name || I18N.shareNativeTitle || '', text, url }).catch(() => {});
	} else {
		navigator.clipboard.writeText(url).then(() => showToast(I18N.linkCopied || '', 'success'));
	}
}

function showThankYouScreen() {
	const dogName = state.dog.name || I18N.defaultDogName || '';
	const nameEl = document.getElementById('thankyouDogName');
	const subtitleEl = document.getElementById('thankyouSubtitleText');

	if (nameEl) nameEl.textContent = dogName;

	if (subtitleEl) {
		const template = subtitleEl.getAttribute('data-template') || '';

		if (template) {
			subtitleEl.textContent = template.replace(/\[\[name\]\]/g, dogName);
		}
	}

	goToScreen(9);
}

function showThankYouResetOptions() {
	showResetOptions();
}

function showResetOptions() {
	const dogName = state.dog.name;
	const recipeMakerRoot = document.querySelector('.recipe-maker-section');
	if (!recipeMakerRoot) return;
	let modal = document.getElementById('resetModal');
	if (!modal) {
		modal = document.createElement('div');
		modal.id = 'resetModal';
		modal.className = 'modal-overlay';
		modal.innerHTML = `
          <div class="modal-box reset-modal-box">
            <h3>${I18N.resetTitle || ''}</h3>
            <p>${I18N.resetSubtitle || ''}</p>
            <div class="reset-options">
              ${
					dogName
						? `
              <button class="btn-reset-option text-left" onclick="resetSameDog()">
                <div class="reset-opt-icon">🐾</div>
                <div>
                  <div class="reset-opt-title">${String(I18N.resetSameDogTitle || '').replace('__NAME__', dogName)}</div>
                  <div class="reset-opt-desc">${String(I18N.resetSameDogDesc || '').replace('__NAME__', dogName)}</div>
                </div>
              </button>`
						: ''
				}
              <button class="btn-reset-option text-left" onclick="resetAll()">
                <div class="reset-opt-icon">🐶</div>
                <div>
                  <div class="reset-opt-title">${I18N.resetDifferentDogTitle || ''}</div>
                  <div class="reset-opt-desc">${I18N.resetDifferentDogDesc || ''}</div>
                </div>
              </button>
            </div>
            <button class="btn-ghost-nav align-center" style="width:100%; margin-top:12px" onclick="document.getElementById('resetModal').remove()">
              ${I18N.cancel || ''}
            </button>
          </div>
        `;
		recipeMakerRoot.appendChild(modal);
		modal.addEventListener('click', (e) => {
			if (e.target === modal) modal.remove();
		});
	} else {
		modal.style.display = 'flex';
	}
}

function resetSameDog() {
	recipeGenerated = false;
	const savedDog = { ...state.dog };
	const savedNewsletter = state.recipe.newsletter;
	state.selected = { meats: [], veggies: [], organs: [], bones: [] };
	const savedCustomerFirstName = state.recipe.customerFirstName;
	const savedCustomerLastName = state.recipe.customerLastName;
	state.recipe = {
		name: `${savedDog.name}'s New Recipe`,
		story: '',
		newsletter: savedNewsletter,
		email: '',
		customerFirstName: savedCustomerFirstName,
		customerLastName: savedCustomerLastName,
	};
	state.days = 1;
	state.proportions = { ...DEFAULT_PROPORTIONS };
	state.dismissedZeroPortion = { meats: false, veggies: false, organs: false, bones: false };
	state.skippedStepCategories = { meats: false, veggies: false, organs: false, bones: false };
	renderIngredients('meats');
	renderIngredients('veggies');
	renderIngredients('organs');
	renderIngredients('bones');
	updateCounts();
	const modal = document.getElementById('resetModal');
	if (modal) modal.remove();
	goToScreen(2);
	saveRecipeMakerState();
}

function resetAll() {
	recipeGenerated = false;
	clearRecipeMakerState();
	state.selected = { meats: [], veggies: [], organs: [], bones: [] };
	state.dog = {
		name: '',
		gender: 'Male',
		isFeedingRaw: false,
		weight: null,
		weightUnit: 'lbs',
		age: 'adult',
		bodyCondition: 'healthy-weight',
		bodyAdj: 0,
	};
	state.recipe = { name: '', story: '', newsletter: 'yes', email: '', customerFirstName: '', customerLastName: '' };
	state.days = 1;
	state.proportions = { ...DEFAULT_PROPORTIONS };
	state.dismissedZeroPortion = { meats: false, veggies: false, organs: false, bones: false };
	state.skippedStepCategories = { meats: false, veggies: false, organs: false, bones: false };
	renderIngredients('meats');
	renderIngredients('veggies');
	renderIngredients('organs');
	renderIngredients('bones');
	updateStepPortionUI('meats');
	updateStepPortionUI('veggies');
	updateStepPortionUI('organs');
	updateStepPortionUI('bones');
	renderBodyConditionCards();
	updateCounts();
	syncFeedingExperienceToggle();
	const modal = document.getElementById('resetModal');
	if (modal) modal.remove();
	goToScreen(0);
}

function showToast(msg, type = 'info', vars = null) {
	let toast = document.getElementById('appToast');
	const sectionRoot = document.querySelector('.recipe-maker-section');
	if (!sectionRoot) return;
	if (!toast) {
		toast = document.createElement('div');
		toast.id = 'appToast';
		sectionRoot.appendChild(toast);
	}

	const overrides = vars != null && typeof vars === 'object' && !Array.isArray(vars) ? vars : {};
	const text = formatToastMessage(msg, overrides);

	toast.textContent = text;
	toast.className = `app-toast ${type}`;
	toast.style.display = 'block';
	setTimeout(() => {
		toast.style.display = 'none';
	}, 3500);
}

(function () {
	const t = document.querySelector('[data-theme-toggle]');
	const r = document.documentElement;
	let d = matchMedia('(prefers-color-scheme:dark)').matches ? 'dark' : 'light';
	r.setAttribute('data-theme', d);
	const sunSvg =
		'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>';
	const moonSvg =
		'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>';
	if (t) {
		t.innerHTML = d === 'dark' ? sunSvg : moonSvg;
		t.addEventListener('click', () => {
			d = d === 'dark' ? 'light' : 'dark';
			r.setAttribute('data-theme', d);
			t.innerHTML = d === 'dark' ? sunSvg : moonSvg;
		});
	}
})();

function initRecipeMakerApp() {
	const yr = document.getElementById('copyrightYear');
	if (yr) yr.textContent = new Date().getFullYear();

	document.querySelectorAll('.intro-benefits li').forEach((item) => item.classList.add('position-relative'));

	detectCurrency();
	if (IS_THEME_EDITOR) document.documentElement.classList.add('recipe-maker-theme-editor');
	renderIngredients('meats');
	renderIngredients('veggies');
	renderIngredients('organs');
	renderIngredients('bones');
	renderBodyConditionCards();

	if (decodeRecipeFromURL()) {
		recipeGenerated = true;
		renderIngredients('meats');
		renderIngredients('veggies');
		renderIngredients('organs');
		renderIngredients('bones');
		updateCounts();
		state.days = 1;
		syncDayCounters();
		// 'keep' preserves the ?recipe= URL so the shared recipe survives a reload.
		goToScreen(8, { instant: true, skipPersist: true, historyMode: 'keep' });
		renderRecipe();
	} else {
		const savedState = loadRecipeMakerState();

		if (savedState) {
			applyRestoredState(savedState);
			renderIngredients('meats');
			renderIngredients('veggies');
			renderIngredients('organs');
			renderIngredients('bones');
			renderBodyConditionCards();
			hydrateUIFromState();
		} else {
			state.recipe.newsletter = 'yes';
		}

		const requestedScreen = getRequestedScreenFromLocation();
		let initialScreen = savedState ? savedState.currentScreen : 0;
		let blockedScreen = null;

		if (requestedScreen != null && requestedScreen !== initialScreen) {
			if (requestedScreen <= getMaxReachableScreen()) {
				initialScreen = requestedScreen;
			} else {
				blockedScreen = requestedScreen;
			}
		}

		// Read the resume options before displaying a blocked step, since showing it
		// moves state.currentScreen away from where the visitor actually left off.
		const resumeOptions = blockedScreen == null ? null : getResumeGuardOptions();

		if (savedState || initialScreen > 0 || requestedScreen != null) {
			// A blocked step is still displayed, so the view matches the URL; the guard
			// modal on top is what moves the visitor to a step they can use.
			const displayScreen = blockedScreen == null ? initialScreen : getBlockedDisplayScreen(blockedScreen, initialScreen);

			// Fill placeholders before the screen is revealed, never after.
			renderGeneratedScreen(displayScreen);
			goToScreen(displayScreen, {
				instant: true,
				skipPersist: true,
				historyMode: blockedScreen == null ? 'replace' : 'keep',
			});
		}

		updateCounts();
		syncDayCounters();
		updateSkipNextButtons();

		if (blockedScreen != null) {
			showResumeGuardModal(blockedScreen, resumeOptions);
		}
	}

	trackRecipeMakerStep(state.currentScreen);

	initStickyNavRows();

	const rmRoot = document.querySelector('.recipe-maker-section');
	if (rmRoot) {
		let blendPtr = null;
		rmRoot.addEventListener(
			'pointerdown',
			(e) => {
				const btn = e.target.closest('.blend-card');
				if (!btn || !rmRoot.contains(btn)) return;
				blendPtr = { x: e.clientX, y: e.clientY, id: e.pointerId, el: btn };
			},
			true,
		);
		rmRoot.addEventListener(
			'pointerup',
			(e) => {
				if (!blendPtr || blendPtr.id !== e.pointerId) {
					blendPtr = null;
					return;
				}
				const btn = e.target.closest('.blend-card');
				if (!btn || btn !== blendPtr.el) {
					blendPtr = null;
					return;
				}
				if (Math.hypot(e.clientX - blendPtr.x, e.clientY - blendPtr.y) <= POINTER_ACTIVATION_MAX_MOVE_PX) {
					const cat = btn.dataset.category;
					const num = parseInt(btn.dataset.blend, 10);
					if (cat && num) {
						selectBlend(cat, num);
						btn._rmSuppressBlendClick = true;
						requestAnimationFrame(() => {
							requestAnimationFrame(() => {
								btn._rmSuppressBlendClick = false;
							});
						});
					}
				}
				blendPtr = null;
			},
			true,
		);
		rmRoot.addEventListener('pointercancel', () => {
			blendPtr = null;
		});
		rmRoot.addEventListener(
			'click',
			(e) => {
				const btn = e.target.closest('.blend-card');
				if (btn && btn._rmSuppressBlendClick) {
					e.preventDefault();
					e.stopPropagation();
				}
			},
			true,
		);
		rmRoot.addEventListener('click', (e) => {
			if (e.target.closest('.start-recipe-btn')) {
				triggerRecipeMakerConvertConversion(CONVERT_RECIPE_MAKER_START_CONVERSION);
			}
		});
	}

	if (IS_THEME_EDITOR) {
		document.querySelectorAll('.screen').forEach((screen) => screen.classList.add('active'));
	}
}

/** Drops the boot loader put up by recipe-maker-section.liquid for ?step= URLs. */
function revealRecipeMakerAfterBoot() {
	document.documentElement.classList.remove('rm-step-boot');
}

function bootRecipeMakerApp() {
	try {
		initRecipeMakerApp();
	} finally {
		revealRecipeMakerAfterBoot();
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', bootRecipeMakerApp);
} else {
	// This script is deferred, so the DOM is already parsed when it runs; initializing
	// now instead of on DOMContentLoaded skips waiting for the remaining deferred bundles.
	bootRecipeMakerApp();
}

window.goToScreen = goToScreen;
window.validateAndNext = validateAndNext;
window.skipIngredientStep = skipIngredientStep;
window.selectBlend = selectBlend;
window.selectToggle = selectToggle;
window.selectFeedingExperience = selectFeedingExperience;
window.validateDogDetails = validateDogDetails;
window.buildAndShowRecipe = buildAndShowRecipe;
window.adjustDays = adjustDays;
window.printRecipe = printRecipe;
// window.emailRecipe = emailRecipe;
// window.closeEmailModal = closeEmailModal;
// window.sendRecipeEmail = sendRecipeEmail;
window.copyShareLink = copyShareLink;
window.shareRecipe = shareRecipe;
window.showResetOptions = showResetOptions;
window.showThankYouScreen = showThankYouScreen;
window.showThankYouResetOptions = showThankYouResetOptions;
window.resetSameDog = resetSameDog;
window.resetAll = resetAll;
window.openPortionEditor = openPortionEditor;
window.closePortionModal = closePortionModal;
window.resetPortionsToDefaults = resetPortionsToDefaults;
window.savePortions = savePortions;
window.dismissPortionOverlay = dismissPortionOverlay;
window.continueFromZeroOverlay = continueFromZeroOverlay;
